import { BrightSpan } from '@components/BrightSpan/BrightSpan';
import {
  ChangeSubscriptionConfirmationModal,
  ChangeSubscriptionConfirmationModalRef,
} from '@components/ConfirmationModal/ChangeSubscriptionConfirmationModal';
import { CreditsPlusIcon } from '@components/Icons/CreditsPlusIcon';
import { MultipleToggle } from '@components/MultipleToggle/MultipleToggle';
import { UserMenu } from '@components/UserMenu/UserMenu';
import { BackTo } from '@components/util/BackToPage';
import { useWindowSize } from '@lib/hooks/sizing/window-size';
import { useIsEmbedded } from '@lib/hooks/useIsEmbedded';
import { useServiceWorkerAuth } from '@lib/hooks/useServiceWorkerAuth';
import { PlanApproxNote } from '@lib/planFeatures';
import { determineCtaProps } from '@lib/pricing';
import {
  handleCreditsPurchase,
  handlePortalClick,
  handleSubscriptionChange,
  handleSubscriptionPurchase,
} from '@lib/purchase';
import { formatCreditsWithUnit, formatDurationToString, formatPrice } from '@lib/utils/units';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CenterFocusStrongRoundedIcon from '@mui/icons-material/CenterFocusStrongRounded';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import SubtitlesOutlinedIcon from '@mui/icons-material/SubtitlesOutlined';
import { CreditsBox } from '@pages/App/components/Nav/CreditsBox';
import {
  billingStore,
  COST_APPROXIMATE_INGEST_PER_HOUR_IN_CREDITS,
  Product,
  SECONDS_IN_HOUR,
} from '@stores/Billing';
import { paywallStore } from '@stores/Paywall';
import { trackingStore } from '@stores/Tracking';
import { userStore } from '@stores/User';
import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EnterpriseTierEntry } from './components/EnterpriseTierEntry/EnterpriseTierEntry';
import { PlanFeatures } from './components/PlanFeatures/PlanFeatures';
import { TierEntry } from './components/TierEntry/TierEntry';
import { TierGroup } from './components/TierGroup/TierGroup';
import css from './PricingPage.module.scss';

// Credit packs are sold on top of a plan; the backend takes the order either way, so the
// refusal is the button's, and it says why.
const TOP_UP_NEEDS_PLAN_REASON = 'Available with an active plan.';

/**
 * Pricing page.
 *
 * NOTE: this page is embedded in the marketing landing inside an <iframe> via `?embed=1`
 * (see `useIsEmbedded`). When editing, keep the embedded context in mind:
 *  - In embed mode we add ~28px top padding (to clear the landing's overlay nav) and show a
 *    "Logged in as <email>. Not you?" line — keep both embed-only.
 *  - Any CTA that navigates to a DIFFERENT PAGE must open a NEW WINDOW in embed mode (break out
 *    of the iframe): route it through `navigateCta`, not `navigate`. Modals stay in-iframe; the
 *    Stripe checkout already opens its own new window, so it needs no embed-specific handling.
 */
export const PricingPage: React.FC = observer(() => {
  const userEmail = userStore.data?.email;
  const navigate = useNavigate();
  const [sliderValue, setSliderValue] = useState<number>(1);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [availableSubscriptions, setAvailableSubscriptions] = useState<Product[]>([]);
  const [availableOneTimeProducts, setAvailableOneTimeProducts] = useState<Product[]>([]);
  const [pendingSubscriptionChange, setPendingSubscriptionChange] = useState<{
    product: Product;
    action: 'downgrade' | 'upgrade';
  } | null>(null);
  const changeSubscriptionConfirmationModal = useRef<ChangeSubscriptionConfirmationModalRef>(null);
  useEffect(() => {
    trackingStore.pricingPageViewed();
    async function fetchAvailableSubscriptions() {
      const result = await billingStore.getAvailableSubscriptions();
      setAvailableSubscriptions(result as Product[]);
    }
    async function fetchAvailableOneTimeProducts() {
      const result = await billingStore.getAvailableOneTimeProducts();
      setAvailableOneTimeProducts(result as Product[]);
    }
    fetchAvailableSubscriptions();
    fetchAvailableOneTimeProducts();
  }, []);

  useServiceWorkerAuth();

  // The TierGroup stacks into a single column below 900px; when stacked, the
  // long credit breakdowns collapse behind a "Show all features" toggle —
  // except the highlighted plan, which stays expanded as the full-detail option.
  // Width-based (not a separate mobile mode) so it follows the actual layout.
  const [windowWidth] = useWindowSize();
  const isStacked = windowWidth < 900;
  // Matches the SCSS `mobile` breakpoint. Used to shrink the Monthly/Yearly
  // toggle's generous desktop padding so it can't force a horizontal scroll on
  // narrow screens (the page is embedded in the landing iframe at full width).
  const isMobile = windowWidth <= 768;
  // Embedded in the landing page's iframe (?embed=1): nudge content below the
  // landing's overlay nav and spell out which account is acting.
  const isEmbedded = useIsEmbedded();

  // In embed mode, a CTA that leaves the pricing page (register, settings, etc.)
  // must break OUT of the iframe into a new window; otherwise navigate in place.
  // Modals stay in-iframe; Stripe checkout already opens its own new window.
  const navigateCta = (path: string) => {
    if (isEmbedded) {
      window.open(path, '_blank', 'noopener');
    } else {
      navigate(path);
    }
  };
  const [expandedPlans, setExpandedPlans] = useState<Set<string>>(new Set());
  const expandPlan = (id: string) =>
    setExpandedPlans((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });

  const scrollToApproxNote = () => {
    document
      .getElementById('amounts-approximate')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const availableSubscriptionsArray = Object.values(availableSubscriptions);
  const availableOneTimeProductsArray = Object.values(availableOneTimeProducts);

  const topUpProduct = availableOneTimeProductsArray.find((p) =>
    p.name.toLowerCase().includes('credits'),
  );

  // Handle hash navigation for scrolling to specific sections
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      // Remove the # from the hash
      const elementId = hash.substring(1);
      const element = document.getElementById(elementId);
      if (element && availableOneTimeProductsArray.length > 0) {
        // Use setTimeout to ensure the element is rendered
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 0);
      }
    }
  }, [availableOneTimeProductsArray]); // Re-run when one-time products are loaded

  const currentSubscription = billingStore.data?.subscription;

  const handleConfirmSubscriptionChange = async () => {
    if (pendingSubscriptionChange) {
      trackingStore.subscriptionChanged({
        action: pendingSubscriptionChange.action,
        newPlanId: pendingSubscriptionChange.product.id,
        newPlanName: pendingSubscriptionChange.product.name,
      });
      await handleSubscriptionChange(pendingSubscriptionChange.product.id);
      setPendingSubscriptionChange(null);
      // Force refresh subscription data to immediately update the pricing page
      await billingStore.forceRefreshBillingData();
    }
  };

  // The paywall's verdict for the logged-in account (null while logged out). A lapsed
  // subscription can't be replaced from here — the backend refuses a second one — so its
  // cards all lead to the Stripe portal, where the lapsed one is repaired.
  const planAccess = paywallStore.access;

  const determineCtaPropsForProduct = (product: Product) => {
    if (planAccess === 'lapsed') {
      return {
        text: 'Manage subscription',
        variant: 'secondary-cta' as const,
        onClick: () => handlePortalClick('pricing-page'),
      };
    }
    return determineCtaProps(
      product,
      billingStore.data?.subscription,
      async (product) => {
        if (planAccess === 'no-plan') {
          // Nothing attached: a first purchase. Outside the landing's iframe it runs in this
          // tab so the paywall can confirm the return; Stripe refuses to load inside an
          // iframe, so the embed keeps the new-tab flow.
          if (isEmbedded) {
            trackingStore.checkoutStarted({ productId: product.id, productType: 'subscription' });
            await handleSubscriptionPurchase(product.id);
          } else {
            await paywallStore.checkout(product.id, 'pricing-page');
          }
          return;
        }
        trackingStore.checkoutStarted({ productId: product.id, productType: 'subscription' });
        setPendingSubscriptionChange({
          product: product,
          action: 'upgrade',
        });
        changeSubscriptionConfirmationModal.current?.open();
      },
      async (product) => {
        const pendingNewProduct = billingStore.availableSubscriptionsData.data[product.id];
        if (!pendingNewProduct) {
          console.error('Pending new product not found');
          return;
        }
        setPendingSubscriptionChange({
          product: pendingNewProduct,
          action: 'downgrade',
        });
        changeSubscriptionConfirmationModal.current?.open();
      },
      navigateCta,
    );
  };

  return (
    <>
      <div className={css.userMenu}>
        <div className={css.creditsBox}>
          You have <CreditsBox opened={true} anchorDirection="below" />
          <button
            className={css.manageBillingButton}
            onClick={() => {
              trackingStore.manageBillingClicked({ source: 'pricing-page' });
              navigateCta('/settings/billing');
            }}
          >
            <SettingsRoundedIcon className={css.cogIcon} />
            <span className={css.buttonText}>Manage billing</span>
          </button>
        </div>
        {userStore.isLoggedIn && <UserMenu />}
      </div>
      {userStore.isLoggedIn && (
        <div className={css.leftBox}>
          <BackTo useBrowserHistory>Back</BackTo>
        </div>
      )}
      <div className={isEmbedded ? `${css.container} ${css.embedded}` : css.container}>
        <main>
          <h1 className={css.header}>StarZero Pricing</h1>
          <p className={css.description}>
            Flexible plans for creators and teams. Beta pricing with early access for initial
            testers
          </p>
          {isEmbedded && userStore.data?.email && (
            <p className={css.embedAccount}>
              Logged in as <strong>{userStore.data.email}</strong>.{' '}
              <button
                type="button"
                className={css.notYou}
                onClick={() =>
                  // On logout API failure, still clear local auth so the UI flips to
                  // logged-out (matches the VerifyEmail.tsx pattern); avoids an
                  // unhandled rejection that would otherwise strand the embed user.
                  userStore.logout({ redirect: false }).catch(() => userStore.setToken(null))
                }
              >
                Not you?
              </button>
            </p>
          )}
          <div className={css.monthlySwitcher}>
            <MultipleToggle
              className={css.billingToggle}
              options={[
                { value: 'monthly', label: 'Monthly', padding: isMobile ? 20 : 48 },
                {
                  value: 'yearly',
                  label: 'Yearly',
                  disabled: true,
                  customLabel: (
                    <span className={css.comingSoonLabel}>
                      Yearly <BrightSpan>Coming soon</BrightSpan>
                    </span>
                  ),
                },
              ]}
              value={billingPeriod}
              onChange={setBillingPeriod}
            />
          </div>
          <TierGroup gap={12} verticalAlign="start">
            {availableSubscriptionsArray.map((subscription, index) => {
              const isHighlighted =
                index === 1 || (index === 0 && availableSubscriptionsArray.length === 1);
              // Desktop: always show. Mobile: only the highlighted plan is expanded
              // by default; others collapse behind "Show all features".
              const showBreakdowns =
                !isStacked || isHighlighted || expandedPlans.has(subscription.id);
              return (
                <TierEntry
                  key={subscription.id}
                  product={subscription}
                  ctaProps={determineCtaPropsForProduct(subscription)}
                  highlighted={isHighlighted}
                  badge={isHighlighted ? 'Best value · most credits per dollar' : undefined}
                  productType="subscription"
                >
                  <PlanFeatures
                    product={subscription}
                    expanded={showBreakdowns}
                    onExpand={() => expandPlan(subscription.id)}
                    onApproxClick={scrollToApproxNote}
                  />
                </TierEntry>
              );
            })}
            <EnterpriseTierEntry source="pricing-page" />
          </TierGroup>

          {/* How usage-billed work is charged — sits right under the comparison cards */}
          <p className={css.agentNote} id="amounts-approximate">
            <span className={css.agentNoteIcon} aria-hidden="true">
              *
            </span>
            <span>
              <PlanApproxNote />
            </span>
          </p>

          {/* Free with every plan */}
          <div className={css.includedStrip}>
            <span className={css.includedStripLabel}>Every plan includes</span>
            <div className={css.includedStripItems}>
              <span className={css.includedItem}>
                <CenterFocusStrongRoundedIcon className={css.includedIcon} />
                Smart auto-reframing
              </span>
              <span className={css.includedItem}>
                <SubtitlesOutlinedIcon className={css.includedIcon} />
                Transcripts &amp; captions
              </span>
              <span className={css.includedItem}>
                <AutoAwesomeIcon className={css.includedIcon} />
                Content analysis on every ingest
              </span>
            </div>
          </div>

          {/* Add-ons: connect your AI + top up credits */}
          <div className={css.bottomBanners}>
            <div className={css.banner}>
              <span className={css.bannerIconChip}>
                <HubOutlinedIcon />
              </span>
              <div className={css.bannerText}>
                <div className={css.bannerHeading}>
                  <h3>Connect your AI</h3>
                  <BrightSpan>Private beta</BrightSpan>
                </div>
                <p>
                  Bring StarZero into ChatGPT, Claude, Cursor &amp; more over MCP. Search, clip and
                  edit your media from your assistant.
                </p>
              </div>
              <a
                className={css.bannerCta}
                href="mailto:sales@starzero.ai?subject=StarZero%20MCP%20private%20beta"
              >
                Request access
              </a>
            </div>

            <div className={css.banner} id="top-up">
              <span className={css.bannerIconChip}>
                <CreditsPlusIcon />
              </span>
              <div className={css.bannerText}>
                <div className={css.bannerHeading}>
                  <h3>Top up credits</h3>
                </div>
                {topUpProduct ? (
                  <p>
                    {formatCreditsWithUnit(topUpProduct.quota.credits)} for{' '}
                    {formatPrice(topUpProduct.price)}, enough for ~
                    {formatDurationToString(
                      (topUpProduct.quota.credits / COST_APPROXIMATE_INGEST_PER_HOUR_IN_CREDITS) *
                        SECONDS_IN_HOUR,
                      { precision: 0, detail: 1 },
                    )}{' '}
                    of extra media ingest. {TOP_UP_NEEDS_PLAN_REASON}
                  </p>
                ) : (
                  <p>
                    Need extra credits on top of your plan? Get in touch and we&apos;ll sort it.
                  </p>
                )}
              </div>
              {topUpProduct ? (
                <button
                  className={css.bannerCta}
                  disabled={paywallStore.isWalled}
                  title={paywallStore.isWalled ? TOP_UP_NEEDS_PLAN_REASON : undefined}
                  onClick={() => {
                    trackingStore.checkoutStarted({
                      productId: topUpProduct.id,
                      productType: 'one-time',
                    });
                    handleCreditsPurchase();
                  }}
                >
                  Top-up
                </button>
              ) : (
                <a
                  className={css.bannerCta}
                  href="mailto:sales@starzero.ai?subject=Credit%20top-up"
                >
                  Contact us
                </a>
              )}
            </div>
          </div>

          {/* <TierGroup gap={16}>
          <ExtraCreditsEntry />
        </TierGroup> */}
          <div className={css.emptySpace} />
        </main>
        <ChangeSubscriptionConfirmationModal
          ref={changeSubscriptionConfirmationModal}
          currentSubscription={currentSubscription}
          newSubscription={pendingSubscriptionChange?.product ?? null}
          action={pendingSubscriptionChange?.action ?? null}
          onConfirm={handleConfirmSubscriptionChange}
          onCancel={() => setPendingSubscriptionChange(null)}
        />
      </div>
    </>
  );
});
